import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Icons } from '../../../shared/helpers/icons';
import { IThreatAssessmentTeam } from '../../../shared/models/contact';
import { ContactsService } from '../../../shared/services/contact.service';
import { LucideAngularModule } from 'lucide-angular';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms'; 
import { UserDataService } from '../../../shared/services/auth/userData.service';
import { SpinnerComponent } from '../spinner/spinner.component';
import { PopupMessageComponent } from '../popup-message/popup-message.component';

@Component({
  selector: 'assess-team',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    SpinnerComponent,
    PopupMessageComponent
  ],
  templateUrl: './assess-team.component.html',
  styleUrl: './assess-team.component.css'
})
export class AssessTeamComponent {

  @Input() caseId: string = '';
  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  icons = Icons;

  searchQuery: string = '';
  selectedContacts: IThreatAssessmentTeam[] = [];
  usersList: IThreatAssessmentTeam[] = [];
  currentUser: IThreatAssessmentTeam | null = null;
  currentBuildingId: string | null = null;
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loadingContacts: boolean = false;
  loadingTeam: boolean = false;
  loading: boolean = false;

  constructor(
    private contactsService: ContactsService,
    private userDataService: UserDataService
  ) { }

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getCurrentUser();
    this.loadAssessUsers();
  };  

  loadAssessUsers(): void {
    this.loading = true;
    if (this.currentBuildingId) {
      this.contactsService.getUsers(this.currentBuildingId).subscribe({
        next: (response) => {
          this.loading = false;

          const buildingId = Number(this.currentBuildingId);
          const email = this.currentUser?.email || '';
          const isInternalUser = email.endsWith('@nside.io');
          const isInternalBuilding = buildingId === 130 || buildingId === 316;

          // Filter users if currentBuildingId is NOT 130, remove all users with email ending in '@nside.io'
          if (!isInternalUser && !isInternalBuilding) {
            this.usersList = response.filter(
              (user: any) => !user.email.endsWith('@nside.io')
            );
          } else {
            this.usersList = response;
          }
          this.loadThreatAssessmentTeamMembers();
        },
        error: (error) => {
          this.loading = false;
          console.error('Error fetching users:', error.message);
        },
      });
    }
  }  

  loadThreatAssessmentTeamMembers(): void {
    this.loadingTeam = true;
    this.updateLoadingState();
    if (this.caseId) {
      this.contactsService.getThreatAssessmentTeam(this.caseId).subscribe({
        next: (res) => {
          this.loadingTeam = false;
          this.updateLoadingState();
          if (res.found != false) {
            this.selectedContacts = res.team
            .map((item: any) => this.usersList.find(contact => contact.user_id === item.user_id))
            .filter((contact: IThreatAssessmentTeam | undefined): contact is IThreatAssessmentTeam => contact !== undefined);
          }
        },
        error: (err) => {
          this.loadingTeam = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = err.message;
          this.updateLoadingState();
        }
      });
    }
  }

  get filteredContacts(): IThreatAssessmentTeam[] {
    if (!this.searchQuery) {
      return this.usersList;
    }
    const lowerQuery = this.searchQuery.toLowerCase();
    return this.usersList.filter(contact =>
      contact.name?.toLowerCase().includes(lowerQuery)
    );
  };

  isContactSelected(contact: IThreatAssessmentTeam): boolean {
    return this.selectedContacts.some(selectedContact => selectedContact.user_id === contact.user_id);
  }

  updateLoadingState() {
    this.loading = this.loadingContacts || this.loadingTeam;
  }

  // add contact to the selected list
  addContactToSelectedList(contact: IThreatAssessmentTeam) {
    if (this.isContactSelected(contact)) {
      alert('This user has been assigned to the team already.');
    } else {
      this.selectedContacts.push(contact);
    }
  };

  // remove contact from selected list
  removeContactFromSelectedList(selectedContact: IThreatAssessmentTeam): void {
    const index = this.selectedContacts.findIndex(contact => contact.name === selectedContact.name);
    if (index !== -1) {
      this.selectedContacts.splice(index, 1);
    }
  };

  saveSelectedUsers(): void {
    if (this.selectedContacts.length < 1) {
      return;
    } else {
      const body = this.selectedContacts.map(contact => ({
      case_id: this.caseId,
      user_id: contact.user_id,
      building_id: Number(this.currentBuildingId),
      }))
      this.contactsService.saveThreatAssessmentTeam(body).subscribe({
        next: (response) => {
          this.loading = false;
          this.onSave.emit({'type': 'success', 'message': 'This occurred after saving the Behavioral Threat Assessment Team.'});
        },
        error: (error) => {
          this.loading = false;
          console.error('Error saving contacts to Threat team:', error.message);
          this.onSave.emit({type: 'failed', 'message': error.message});
        }
      });
    }
  }

  handleCloseTeam(): void {
    this.onClose.emit();
  }

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
    this.onClose.emit();
  }
}
